/**
 * Store des notifications (Notification Service).
 * Utilisé pour le centre de notifications étudiant (receiver_id = slug étudiant).
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Notification } from '@/types/notification'
import { useNotificationService } from '@/composables/useNotificationService'
import { toast } from 'vue3-toastify'

const PREVIEW_LIMIT = 6
const DEFAULT_POLL_MS = 25000
const NEW_TOAST_COOLDOWN_MS = 15000

export const useNotificationsStore = defineStore('notifications', () => {
  /** Kill-switch déploiement : false par défaut (voir nuxt.config → NUXT_PUBLIC_STUDENT_PUSH_NOTIFICATIONS). */
  const studentPushNotificationsEnabled = computed(() => {
    if (import.meta.server) return false
    const c = useRuntimeConfig().public as { studentPushNotificationsEnabled?: boolean }
    return c.studentPushNotificationsEnabled === true
  })

  const items = ref<Notification[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchedSlug = ref<string | null>(null)
  const pollingForSlug = ref<string | null>(null)
  const pollingMs = ref<number>(DEFAULT_POLL_MS)
  const lastPollAt = ref<number>(0)
  const lastNewToastAt = ref<number>(0)
  const knownSlugs = ref<Set<string>>(new Set())
  const intervalId = ref<number | null>(null)

  const unreadCount = computed(() =>
    items.value.filter((n) => n.status !== 'READ').length
  )

  const previewList = computed(() =>
    items.value.slice(0, PREVIEW_LIMIT)
  )

  const unreadPreviewCount = computed(() =>
    previewList.value.filter((n) => n.status !== 'READ').length
  )

  async function load(studentSlug: string | null) {
    if (!studentPushNotificationsEnabled.value) {
      items.value = []
      lastFetchedSlug.value = null
      loading.value = false
      error.value = null
      stopPolling()
      return
    }
    const { fetchNotifications, isConfigured } = useNotificationService()
    if (!studentSlug || !isConfigured) {
      items.value = []
      lastFetchedSlug.value = null
      return
    }
    loading.value = true
    error.value = null
    try {
      const raw = await fetchNotifications(studentSlug)
      const filtered = raw.filter(
        (n: Notification) => n.receiver_id === studentSlug && n.channel === 'push'
      )
      // plus récent au plus ancien
      filtered.sort((a, b) => {
        const ad = new Date(a.created_at).getTime()
        const bd = new Date(b.created_at).getTime()
        return (Number.isNaN(bd) ? 0 : bd) - (Number.isNaN(ad) ? 0 : ad)
      })
      items.value = filtered
      knownSlugs.value = new Set(filtered.map((n) => n.slug))
      lastFetchedSlug.value = studentSlug
    } catch (e: any) {
      error.value = e?.message || 'Erreur chargement des notifications'
      items.value = []
    } finally {
      loading.value = false
    }
  }

  async function refresh(studentSlug: string, opts?: { showToast?: boolean }) {
    if (!studentPushNotificationsEnabled.value) return
    const showToast = opts?.showToast ?? true
    const { fetchNotifications } = useNotificationService()
    const raw = await fetchNotifications(studentSlug)
    const filtered = raw.filter(
      (n: Notification) => n.receiver_id === studentSlug && n.channel === 'push'
    )
    filtered.sort((a, b) => {
      const ad = new Date(a.created_at).getTime()
      const bd = new Date(b.created_at).getTime()
      return (Number.isNaN(bd) ? 0 : bd) - (Number.isNaN(ad) ? 0 : ad)
    })

    const prevKnown = knownSlugs.value
    const nextKnown = new Set(filtered.map((n) => n.slug))
    const newOnes = filtered.filter((n) => !prevKnown.has(n.slug))

    items.value = filtered
    knownSlugs.value = nextKnown
    lastFetchedSlug.value = studentSlug

    if (showToast && newOnes.length > 0 && typeof window !== 'undefined') {
      const now = Date.now()
      if (now - lastNewToastAt.value > NEW_TOAST_COOLDOWN_MS) {
        lastNewToastAt.value = now

        const isMobile = window.innerWidth <= 576
        const message =
          newOnes.length === 1
            ? 'Nouvelle notification'
            : `${newOnes.length} nouvelles notifications`

        toast.info(message, {
          autoClose: 3000,
          pauseOnHover: !isMobile,
          closeOnClick: true,
          hideProgressBar: isMobile,
          position: isMobile ? 'bottom-center' : 'top-right',
        })
      }
    }
  }

  function startPolling(studentSlug: string, opts?: { pollMs?: number; showToast?: boolean }) {
    if (import.meta.server) return
    if (!studentPushNotificationsEnabled.value) return
    const pollMs = opts?.pollMs ?? DEFAULT_POLL_MS
    pollingMs.value = pollMs

    // si déjà en cours sur le même slug, ne rien faire
    if (intervalId.value && pollingForSlug.value === studentSlug) return

    stopPolling()
    pollingForSlug.value = studentSlug

    // première mise à jour immédiate (sans toast)
    refresh(studentSlug, { showToast: false }).catch(() => {})

    intervalId.value = window.setInterval(async () => {
      if (!pollingForSlug.value) return
      // éviter de poll quand l'onglet est caché
      if (typeof document !== 'undefined' && document.hidden) return

      const now = Date.now()
      if (now - lastPollAt.value < Math.max(5000, pollMs - 1000)) return
      lastPollAt.value = now

      try {
        await refresh(pollingForSlug.value, { showToast: opts?.showToast ?? true })
      } catch {
        // silence (ne pas spammer l'UI)
      }
    }, pollMs)
  }

  function stopPolling() {
    if (intervalId.value) {
      clearInterval(intervalId.value)
      intervalId.value = null
    }
    pollingForSlug.value = null
  }

  function setItems(list: Notification[]) {
    items.value = list
  }

  async function markRead(slug: string) {
    if (!studentPushNotificationsEnabled.value) return
    const { markAsRead } = useNotificationService()
    try {
      await markAsRead(slug)
      const idx = items.value.findIndex((i) => i.slug === slug)
      if (idx !== -1) {
        const next = [...items.value]
        next[idx] = { ...next[idx], status: 'READ' as const, read_at: new Date().toISOString() }
        items.value = next
      }
    } catch (e) {
      console.error('markRead failed', e)
    }
  }

  async function remove(slug: string) {
    if (!studentPushNotificationsEnabled.value) return
    const { deleteNotification } = useNotificationService()
    try {
      await deleteNotification(slug)
      items.value = items.value.filter((i) => i.slug !== slug)
    } catch (e) {
      console.error('delete notification failed', e)
    }
  }

  function clear() {
    items.value = []
    lastFetchedSlug.value = null
    error.value = null
    knownSlugs.value = new Set()
    stopPolling()
  }

  const isConfigured = computed(() => {
    if (import.meta.server) return false
    const config = useRuntimeConfig()
    return !!(config.public as any).notificationServiceUrl
  })

  return {
    items,
    loading,
    error,
    lastFetchedSlug,
    unreadCount,
    previewList,
    unreadPreviewCount,
    studentPushNotificationsEnabled,
    load,
    refresh,
    startPolling,
    stopPolling,
    setItems,
    markRead,
    remove,
    clear,
    isConfigured,
  }
})
